import React, { useEffect, useState } from "react";
import { Loader2, FileText, Download, Trash2, Plus, Building2, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";

function Loading() { return <div className="py-20 grid place-items-center"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>; }
function Title({ children }) { return <h1 className="h-display text-3xl text-white mb-6">{children}</h1>; }

export function AdminBorrowers() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/borrowers").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return <Loading />;
  return (
    <div data-testid="admin-borrowers">
      <Title>Borrowers</Title>
      <div className="space-y-2">
        {rows.map((u) => (
          <div key={u.user_id} className="card-surface rounded-xl px-4 py-3 flex items-center justify-between">
            <div><span className="text-white font-semibold">{u.name || "—"}</span><span className="text-faint text-sm ml-3">{u.email}</span></div>
            <span className="text-xs text-faint">{u.company} {u.phone && `· ${u.phone}`}</span>
          </div>
        ))}
        {rows.length === 0 && <p className="text-faint">No borrowers registered yet.</p>}
      </div>
    </div>
  );
}

export function AdminReferrals() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/referrals").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return <Loading />;
  return (
    <div data-testid="admin-referrals">
      <Title>Referral Partners</Title>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.id} className="card-surface rounded-xl p-4">
            <div className="flex justify-between"><span className="text-white font-semibold">{r.name} {r.company && <span className="text-faint font-normal">· {r.company}</span>}</span><span className="text-xs text-gold">{r.partner_type}</span></div>
            <div className="text-sm text-dim mt-1">{r.email} {r.phone && `· ${r.phone}`} {r.state && `· ${r.state}`} {r.monthly_volume && `· Vol: ${r.monthly_volume}`}</div>
            {r.message && <div className="text-sm text-white/70 mt-2">{r.message}</div>}
          </div>
        ))}
        {rows.length === 0 && <p className="text-faint">No referral applications yet.</p>}
      </div>
    </div>
  );
}

export function AdminContacts() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/contacts").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return <Loading />;
  return (
    <div data-testid="admin-contacts">
      <Title>Contact Inquiries</Title>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.id} className="card-surface rounded-xl p-4">
            <div className="flex justify-between"><span className="text-white font-semibold">{r.name}</span><span className="text-xs text-gold">{r.reason}</span></div>
            <div className="text-sm text-dim mt-1">{r.email} {r.phone && `· ${r.phone}`} {r.company && `· ${r.company}`} {r.state && `· ${r.state}`}</div>
            {r.message && <div className="text-sm text-white/70 mt-2">{r.message}</div>}
            <div className="text-xs text-faint mt-2">{new Date(r.created_at).toLocaleString()}</div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-faint">No inquiries yet.</p>}
      </div>
    </div>
  );
}

function DocGroup({ g, idx }) {
  const [open, setOpen] = useState(idx < 3);
  const f = g.file;
  return (
    <div className="card-surface rounded-xl overflow-hidden" data-testid={`doc-group-${g.key}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-surface/60 transition-colors" data-testid={`doc-group-toggle-${g.key}`}>
        <span className="min-w-0">
          <span className="block text-white text-sm font-medium truncate">{f ? (f.property_label && f.property_label !== "N/A" ? f.property_label : f.applicant_name) : g.title}</span>
          <span className="block text-faint text-xs truncate">{f ? `${f.applicant_name} · ${f.reference} · ${f.status || ""}` : "Uploaded but not attached to a deal file"}</span>
        </span>
        <span className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-gold border border-gold/40 rounded-full px-2 py-0.5">{g.docs.length}</span>
          <ChevronDown className={`w-4 h-4 text-faint transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div className="border-t border-edge divide-y divide-edge/60">
          {g.docs.map((d) => (
            <div key={d.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
              <span className="text-white/85 text-sm flex items-center gap-2 min-w-0"><FileText className="w-4 h-4 text-gold shrink-0" /><span className="truncate">{d.original_filename}</span>
                <span className="text-faint text-xs shrink-0">{d.category}</span></span>
              <a href={`${API}/files/${d.id}/download`} target="_blank" rel="noreferrer" className="text-gold text-xs inline-flex items-center gap-1 shrink-0" data-testid={`doc-download-${d.id}`}><Download className="w-3.5 h-3.5" /> Download</a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminDocuments() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/documents").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return <Loading />;
  const groups = {};
  rows.forEach((d) => {
    const key = d.file ? d.file.reference : "unattached";
    groups[key] = groups[key] || { key, file: d.file, title: "Not attached to a file", docs: [] };
    groups[key].docs.push(d);
  });
  const needle = q.trim().toLowerCase();
  const list = Object.values(groups)
    .filter((g) => !needle || [g.file?.applicant_name, g.file?.property_label, g.file?.reference, ...g.docs.map((d) => d.original_filename)]
      .some((x) => (x || "").toLowerCase().includes(needle)))
    .sort((a, b) => (a.key === "unattached") - (b.key === "unattached"));
  return (
    <div data-testid="admin-documents">
      <Title>Documents</Title>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by property, borrower, reference or file name" className="bg-surface/70 border-edge text-white h-11 mb-4 max-w-xl" data-testid="doc-search" />
      <div className="space-y-3">
        {list.map((g, i) => <DocGroup key={g.key} g={g} idx={i} />)}
        {list.length === 0 && <p className="text-faint">{rows.length ? "No matching files." : "No documents uploaded yet."}</p>}
      </div>
    </div>
  );
}

export function AdminMessages() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/messages").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return <Loading />;
  return (
    <div data-testid="admin-messages">
      <Title>All Messages</Title>
      <div className="space-y-2">
        {rows.map((m) => (
          <div key={m.id} className="card-surface rounded-xl px-4 py-3">
            <div className="flex justify-between text-xs text-faint"><span className="font-mono text-gold">{m.submission_ref}</span><span>{new Date(m.created_at).toLocaleString()}</span></div>
            <div className="text-sm text-white/85 mt-1"><span className={m.sender_role === "staff" ? "text-gold" : "text-info"}>{m.sender_name}:</span> {m.body}</div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-faint">No messages yet.</p>}
      </div>
    </div>
  );
}

const EMPTY_PARTNER = { name: "", logo_url: "", website: "", products: "", states: "", description: "", is_public: false };

export function AdminPartners() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(EMPTY_PARTNER);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/partners").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const set = (n, v) => setForm((s) => ({ ...s, [n]: v }));

  const save = async () => {
    if (!form.name) { toast.error("Partner name required"); return; }
    try {
      if (editing) await api.put(`/admin/partners/${editing}`, form);
      else await api.post("/admin/partners", form);
      toast.success("Saved"); setForm(EMPTY_PARTNER); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const edit = (p) => { setForm({ ...EMPTY_PARTNER, ...p }); setEditing(p.id); window.scrollTo(0, 0); };
  const del = async (id) => { await api.delete(`/admin/partners/${id}`); load(); };

  if (!rows) return <Loading />;
  return (
    <div data-testid="admin-partners">
      <Title>Capital Partners</Title>
      <p className="text-dim -mt-4 mb-6 text-sm">Only partners marked public appear on the website. Do not enter compensation or private rate sheets.</p>

      <div className="card-surface rounded-2xl p-6 mb-8">
        <div className="eyebrow mb-3">{editing ? "Edit Partner" : "Add Partner"}</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Partner Name" className="bg-surface/70 border-edge text-white h-11" data-testid="partner-form-name" />
          <Input value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} placeholder="Logo URL" className="bg-surface/70 border-edge text-white h-11" />
          <Input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="Website" className="bg-surface/70 border-edge text-white h-11" />
          <Input value={form.products} onChange={(e) => set("products", e.target.value)} placeholder="Products (e.g. DSCR, Bridge)" className="bg-surface/70 border-edge text-white h-11" />
          <Input value={form.states} onChange={(e) => set("states", e.target.value)} placeholder="States" className="bg-surface/70 border-edge text-white h-11" />
          <div className="flex items-center gap-3 px-1">
            <Switch checked={form.is_public} onCheckedChange={(v) => set("is_public", v)} data-testid="partner-form-public" />
            <span className="text-sm text-white/85">Public (show on website)</span>
          </div>
          <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Short description" className="bg-surface/70 border-edge text-white sm:col-span-2" rows={2} />
        </div>
        <div className="flex gap-2 mt-4">
          <Button onClick={save} className="btn-gold rounded-full h-11 px-6 gap-1" data-testid="partner-form-save"><Plus className="w-4 h-4" /> {editing ? "Update" : "Add"} Partner</Button>
          {editing && <Button variant="ghost" onClick={() => { setForm(EMPTY_PARTNER); setEditing(null); }} className="text-faint">Cancel</Button>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((p) => (
          <div key={p.id} className="card-surface rounded-xl p-5" data-testid={`admin-partner-${p.id}`}>
            <div className="flex items-start justify-between">
              {p.logo_url ? <img src={p.logo_url} alt="" className="h-8 object-contain" /> : <Building2 className="w-7 h-7 text-gold" />}
              <span className={`text-xs px-2 py-0.5 rounded-full border ${p.is_public ? "text-ok border-ok/40" : "text-faint border-edge"}`}>{p.is_public ? "Public" : "Private"}</span>
            </div>
            <h3 className="text-white font-bold mt-3">{p.name}</h3>
            {p.description && <p className="text-xs text-dim mt-1">{p.description}</p>}
            <div className="flex gap-3 mt-3">
              <button onClick={() => edit(p)} className="text-xs text-gold">Edit</button>
              <button onClick={() => del(p.id)} className="text-xs text-brandred inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-faint">No partners added yet.</p>}
      </div>
    </div>
  );
}

const FIELDS = [
  ["legal_entity_name", "Legal Entity Name"], ["business_address", "Business Address"], ["phone", "Phone"], ["email", "Contact Email"],
  ["licenses", "Licenses"], ["nmls_number", "NMLS Number"], ["states_served", "States Served"],
  ["general_disclosure", "General Disclosure", true], ["privacy_policy", "Privacy Policy", true], ["terms_of_use", "Terms of Use", true],
  ["electronic_consent", "Electronic Communications Consent", true], ["sms_consent", "SMS Consent", true], ["state_disclosures", "Required State Disclosures", true],
];

export function AdminContent() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <Loading />;
  const isAdmin = user?.role === "admin";
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/settings", s); setS(data); toast.success("Content saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Only admins can edit content"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="admin-content">
      <Title>Website Content & Compliance</Title>
      {!isAdmin && <p className="text-warn text-sm mb-4">Only admins can edit these fields.</p>}
      <div className="card-surface rounded-2xl p-6 space-y-5 max-w-3xl">
        {FIELDS.map(([k, label, area]) => (
          <div key={k}>
            <label className="text-sm text-white/85 mb-1.5 block">{label}</label>
            {area ? (
              <Textarea value={s[k] || ""} onChange={(e) => set(k, e.target.value)} rows={3} disabled={!isAdmin} className="bg-surface/70 border-edge text-white" data-testid={`content-${k}`} />
            ) : (
              <Input value={s[k] || ""} onChange={(e) => set(k, e.target.value)} disabled={!isAdmin} className="bg-surface/70 border-edge text-white h-11" data-testid={`content-${k}`} />
            )}
          </div>
        ))}
        {isAdmin && <Button onClick={save} disabled={busy} className="btn-gold rounded-full h-12 px-8" data-testid="content-save">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save Content"}</Button>}
      </div>
    </div>
  );
}

export function AdminSettings() {
  const { user } = useAuth();
  const [staff, setStaff] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("processor");
  const isAdmin = user?.role === "admin";
  const load = () => api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => setStaff([]));
  useEffect(() => { if (isAdmin) load(); else setStaff([]); }, [isAdmin]);
  const assign = async () => {
    try { await api.put("/admin/staff/role", { email, role }); toast.success("Role updated"); setEmail(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!staff) return <Loading />;
  return (
    <div data-testid="admin-settings">
      <Title>Settings</Title>
      <div className="card-surface rounded-2xl p-6 max-w-2xl">
        <div className="eyebrow mb-2">Your Account</div>
        <p className="text-sm text-white/85">{user?.email} · <span className="text-gold">{user?.role}</span></p>
      </div>
      {isAdmin && (
        <div className="card-surface rounded-2xl p-6 max-w-2xl mt-6">
          <div className="eyebrow mb-3">Team Roles</div>
          <p className="text-sm text-dim mb-4">Grant an existing account a staff role (admin, processor, loan_officer) or reset to borrower. The user must already have an account.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@email.com" className="bg-surface/70 border-edge text-white h-11 flex-1" data-testid="settings-role-email" />
            <select value={role} onChange={(e) => setRole(e.target.value)} className="h-11 rounded-md bg-surface/70 border border-edge text-white px-3 text-sm" data-testid="settings-role-select">
              {["admin", "processor", "loan_officer", "borrower"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <Button onClick={assign} className="btn-gold rounded-lg h-11 px-5" data-testid="settings-role-assign">Set Role</Button>
          </div>
          <div className="mt-5 space-y-2">
            {staff.map((u) => (
              <div key={u.user_id} className="flex justify-between text-sm border-b border-edge/60 py-2">
                <span className="text-white">{u.email}</span><span className="text-gold">{u.role}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
